import { IPrebidAdUnit, IBidResponse } from '../../typings/IPrebid'
import { deepcopy } from '../helpers/deepcopy'
import { Device } from '../helpers/Device'
import { Slot } from '../slots/Slot'
import VideoPrebidManager from '../prebidFacade/videoBids/VideoPrebidManager'
import { IVideoAdTagOptions } from '../../typings/IVideoOptions'
import { LiftTest } from '../liftTest/LiftTest'
import { Mime } from '../enums/ContentType'
import { Placement } from '../enums/OpenRTB'
import { getContentWidth } from '../helpers/DOM'
import { AuctionHouse } from '../auctionHouse/AuctionHouse'
import { OutstreamRenderer } from './OutstreamRenderer'
import { getQueryParam } from '../helpers/getQueryParam'
import { IMAFacade } from '../ImaFacade/ImaFacade'

// eslint-disable-next-line no-magic-numbers
const videoRatio = 9 / 16
const maxOutstreamWidth = 540
const maxContentOutstreamWidth = 540
const maxRecipeOutstreamWidth = 300
const maxSidebarOutstreamWidth = 300
export const maxAdhesionOutstreamWidth = 178
const loglevel = getQueryParam('loglevel', window.location.search)
export const IMA_CDN =
  loglevel === 'debug'
    ? 'https://imasdk.googleapis.com/js/sdkloader/ima3_debug.js'
    : 'https://imasdk.googleapis.com/js/sdkloader/ima3.js'
/**
 * Adds outstream bid requests to slot if
 * it hasn't rendered more times than allowed.
 */

export const hasOutstream = (target: Constructor<Slot>): any =>
  class extends target {
    outstreamRenderer: OutstreamRenderer

    validate(): boolean {
      return super.validate()
    }

    adunits(): Array<IPrebidAdUnit> {
      const adunits: Array<IPrebidAdUnit> = []

      if (meetsOutstreamCriteria(this)) {
        const outstreamAdunit = this.getOutstreamAdunit()
        if (outstreamAdunit) {
          adunits.push(outstreamAdunit)
        }

        const bannerOutstreamAdunit = this.getIBVAdunit()
        if (bannerOutstreamAdunit) {
          adunits.push(bannerOutstreamAdunit)
        }
      }

      adunits.push(...super.adunits())

      return adunits
    }

    private getOutstreamAdunit(): IPrebidAdUnit | undefined {
      let adunitName: string

      if (this.slotName.indexOf('sidebar') !== -1) {
        adunitName = `${this.adUnitId}_outstream`
      } else if (Device.isMobileOrTablet) {
        adunitName = `${this.slotName}_${this.number}_outstream_mobile`
      } else {
        adunitName = `${this.slotName}_${this.number}_outstream_desktop`
      }

      const videoOptions = this.getVideoOptions(adunitName)
      const videoPrebidManager = new VideoPrebidManager(
        videoOptions as IVideoAdTagOptions,
        window.$mediavine.web.model,
        undefined,
        {} as AuctionHouse, // This should be safe for Outstream Bids
        {} as LiftTest
      )
      const outstreamAdunits = videoPrebidManager.getAdUnits()
      const liftTest = LiftTest.new(this.model)

      outstreamAdunits.bids = outstreamAdunits.bids.filter((bid) => {
        if (
          liftTest.bidder === bid.bidder &&
          liftTest.isPrebid() &&
          liftTest.isOutstream()
        ) {
          return false
        }

        bid.isOutstream = true
        bid.outstreamAdUnitCode = adunitName

        return true
      })

      // Set up the renderer for our outstream-video bids

      outstreamAdunits.renderer = {
        url: IMAFacade.IMA_CDN,
        render: (async (bid: IBidResponse) => {
          this.outstreamRenderer =
            this.outstreamRenderer || OutstreamRenderer.new(this)
          await this.outstreamRenderer.loadAd(bid)
        }).bind(this)
      }

      return outstreamAdunits
    }

    /**
     * Get bids requests for Banner Outstream Adunits.
     * i.e.: Triplelift and Yieldmo, who use their own outstream players, not ours.
     */
    private getIBVAdunit(): IPrebidAdUnit | undefined {
      let bidRequestKey
      let adunitName: string
      if (this.slotName.indexOf('sidebar') !== -1) {
        bidRequestKey = `${this.slotName}_ibv_desktop`
        adunitName = `${this.adUnitId}_ibv`
      } else if (Device.isMobileOrTablet) {
        bidRequestKey = `${this.slotName}_ibv_mobile`
        adunitName = `${this.slotName}_${this.number}_ibv_mobile`
      } else {
        bidRequestKey = `${this.slotName}_ibv_desktop`
        adunitName = `${this.slotName}_${this.number}_ibv_desktop`
      }

      const bidRequests = this.model.bidRequests[bidRequestKey]
      if (!bidRequests) {
        return undefined
      }
      const outstreamBidRequests = deepcopy(
        bidRequests,
        'Error copying outstream bid requests'
      )

      if (!outstreamBidRequests) {
        return undefined
      }

      outstreamBidRequests.forEach((bid) => {
        bid.isOutstream = true
        bid.outstreamAdUnitCode = adunitName
      })

      const outstreamSizes = getIBVSizes(this) as AdSize[]
      return {
        code: adunitName,
        bids: outstreamBidRequests,
        mediaTypes: {
          banner: {
            sizes: outstreamSizes
          }
        }
      }
    }

    /**
     * Constructs the Outstream VideoOptions needed for VideoPrebidManager
     * @param adunitName
     */
    private getVideoOptions(adunitName: string): Partial<IVideoAdTagOptions> {
      const [contentWidth, contentHeight] = getOutstreamPlayerSize(
        this,
        this.target?.parentElement
      )

      const videoOptions: Partial<IVideoAdTagOptions> = {
        prebid: true,
        autoplay: true,
        placement: Placement.article, // This is how VideoPrebidManager knows this is an outstream request
        mimes: [Mime.javascript, Mime.mp4],
        contentHeight: contentHeight,
        contentWidth: contentWidth,
        adunitId: adunitName,
        mobile: Device.isMobileOrTablet
      }

      return videoOptions
    }
  }

export function getOutstreamPlayerSize(
  slot: Slot,
  parentElement?: HTMLElement | null
): AdSize {
  const maxWidth = getSlotMaxPlayerWidth(slot)
  const contentWidth = Math.min(
    maxWidth,
    (parentElement && getContentWidth(parentElement)) || maxWidth
  )
  const contentHeight = Math.round(contentWidth * videoRatio)

  return [Math.round(contentWidth), Math.round(contentHeight)]
}

function getSlotMaxPlayerWidth(slot: Slot): number {
  switch (slot.slotName) {
    case 'content':
      return maxContentOutstreamWidth
    case 'recipe':
      return maxRecipeOutstreamWidth
    case 'sticky_sidebar':
      return maxSidebarOutstreamWidth
    case 'adhesion':
      return maxAdhesionOutstreamWidth
    default:
      // Default to the contentCriteria, I guess.
      return maxOutstreamWidth
  }
}

export function meetsOutstreamCriteria(slot: Slot): boolean {
  switch (slot.slotName) {
    case 'content':
      return contentCriteria(slot)
    case 'recipe':
      return recipeCriteria(slot)
    case 'sticky_sidebar':
      return sidebarCriteria(slot)
    case 'adhesion':
      return adhesionCriteria(slot)
    default:
      // Default to the contentCriteria, I guess.
      return contentCriteria(slot)
  }
}

function getIBVSizes(slot: Slot): AdSizes {
  switch (slot.slotName) {
    case 'content':
      return [
        [1, 1],
        [300, 250]
      ]
    case 'recipe':
      return slot.sizes
    case 'sticky_sidebar':
      return [
        [1, 1],
        [300, 250]
      ]
    default:
      return slot.sizes
  }
}
const contentDensityMap = {
  off: Infinity,
  light: 3,
  medium: 2,
  high: 1
}
function contentCriteria(slot: Slot): boolean {
  const densitySetting = slot.model.outstream_density
  const densityNumber = contentDensityMap[densitySetting] || 2

  // eslint-disable-next-line no-magic-numbers
  const runAllSlotsMax = Device.isMobileOrTablet ? 4 : 2
  if (slot.model.optimize_short_form_content && slot.total() > runAllSlotsMax) {
    return true
  }

  const number = slot.number

  return number % densityNumber === 0
}

const recipeDensityMap = {
  off: Infinity,
  light: 2,
  medium: 1,
  high: 1
}
function recipeCriteria(slot: Slot): boolean {
  const densitySetting = slot.model.outstream_density
  const densityNumber = recipeDensityMap[densitySetting] || 1

  const number = slot.number

  return (number + 1) % densityNumber === 0
}

function sidebarCriteria(slot: Slot): boolean {
  const densitySetting = slot.model.outstream_density

  return densitySetting === 'medium'
}

function adhesionCriteria(slot: Slot): boolean {
  // Outstream Only valid on mobile adhesions
  return Device.isMobileOrTablet && slot.model.adhesion_video
}
